import { Field, Form, Formik } from "formik";
import React, { useContext, useEffect, useRef, useState } from "react";
import * as Yup from "yup";

// MUI Components
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  alpha
} from "@mui/material";
import { MuiColorInput } from "mui-color-input";
import { makeStyles } from 'tss-react/mui';

// Icons
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import ForumIcon from '@mui/icons-material/Forum';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import MessageIcon from '@mui/icons-material/Message';
import NumbersIcon from '@mui/icons-material/Numbers';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';

// Context and Services
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import ToastError from "../../toast/error/toastError";
import ToastSuccess from "../../toast/success/toastSuccess";
import { i18n } from "../../translate/i18n";

// Components
import QueueScheduleComponent from "../QueueScheduleComponent";

const useStyles = makeStyles()((theme) => {
  const isDark = theme.palette.mode === 'dark';

  return {
    dialogRoot: {
      '& .MuiDialog-paper': {
        borderRadius: 12,
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.18)',
        maxWidth: 900,
        width: '100%',
        margin: 24,
      },
      '& .MuiBackdrop-root': {
        backgroundColor: alpha(theme.palette.background.default, 0.8)
      }
    },
    dialogTitle: {
      background: isDark
        ? alpha(theme.palette.primary.dark, 0.9)
        : theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      padding: theme.spacing(2, 3),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      '& h6': {
        fontSize: '1.2rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
      }
    },
    dialogContent: {
      padding: theme.spacing(3),
      backgroundColor: isDark
        ? theme.palette.background.default
        : alpha(theme.palette.background.paper, 0.8),
      overflowY: 'auto',
      maxHeight: 'calc(100vh - 290px)',
    },
    dialogActions: {
      padding: theme.spacing(2, 3),
      borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      backgroundColor: isDark
        ? alpha(theme.palette.background.paper, 0.3)
        : alpha(theme.palette.background.default, 0.5),
      display: 'flex',
      justifyContent: 'flex-end',
      gap: theme.spacing(1.5),
    },
    formSection: {
      marginBottom: theme.spacing(3),
      padding: theme.spacing(2.5),
      borderRadius: 12,
      backgroundColor: isDark
        ? alpha(theme.palette.background.paper, 0.2)
        : theme.palette.background.paper,
      boxShadow: isDark
        ? `0 4px 12px ${alpha('#000', 0.1)}`
        : `0 4px 12px ${alpha('#000', 0.05)}`,
      border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
      transition: 'all 0.15s ease-in-out',
      '&:hover': {
        boxShadow: isDark
          ? `0 6px 16px ${alpha('#000', 0.14)}`
          : `0 6px 16px ${alpha('#000', 0.08)}`,
      }
    },
    sectionTitle: {
      marginBottom: theme.spacing(2.5),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.2),
      fontWeight: 600,
      color: theme.palette.text.primary,
      fontSize: '1.1rem',
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
      paddingBottom: theme.spacing(1.2),
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        left: 0,
        bottom: -1,
        width: 40,
        height: 2,
        backgroundColor: theme.palette.primary.main,
        borderRadius: 2
      }
    },
    icon: {
      fontSize: 20,
      color: theme.palette.primary.main,
    },
    textField: {
      '& .MuiOutlinedInput-root': {
        borderRadius: 8,
        backgroundColor: isDark
          ? alpha(theme.palette.background.paper, 0.2)
          : theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.3),
          backgroundColor: isDark
            ? alpha(theme.palette.background.paper, 0.3)
            : theme.palette.background.paper,
        },
        '&.Mui-focused': {
          borderColor: theme.palette.primary.main,
          boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
        }
      },
      '& .MuiOutlinedInput-input': {
        padding: theme.spacing(1.5, 1.5),
        fontSize: '0.9rem',
      },
      '& .MuiInputLabel-root': {
        fontSize: '0.9rem',
        fontWeight: 500,
      },
      '& .MuiFormHelperText-root': {
        marginLeft: 4,
        marginTop: 4,
        fontSize: '0.75rem',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        '&.Mui-error': {
          backgroundColor: alpha(theme.palette.error.main, 0.08),
          color: theme.palette.error.main,
          padding: theme.spacing(0.4, 0.8),
          borderRadius: 4,
        }
      }
    },
    btnWrapper: {
      position: "relative",
    },
    buttonProgress: {
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -12,
      marginLeft: -12,
    },
    saveButton: {
      borderRadius: 8,
      padding: theme.spacing(1, 3),
      fontWeight: 600,
      textTransform: 'none',
      fontSize: '0.9rem',
      boxShadow: theme.shadows[2],
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
        boxShadow: theme.shadows[4],
      }
    },
    cancelButton: {
      borderRadius: 8,
      padding: theme.spacing(1, 2.5),
      fontWeight: 500,
      textTransform: 'none',
      fontSize: '0.9rem',
      color: theme.palette.text.secondary,
      '&:hover': {
        backgroundColor: alpha(theme.palette.divider, 0.1),
      }
    },
    messageTabs: {
      minHeight: 'unset',
      '& .MuiTab-root': {
        textTransform: 'none',
        minHeight: 40,
        padding: theme.spacing(0.8, 1.5),
        fontSize: '0.85rem',
        fontWeight: 500,
        color: theme.palette.text.secondary,
        borderRadius: 6,
        '&.Mui-selected': {
          color: theme.palette.primary.main,
          fontWeight: 600,
          backgroundColor: isDark
            ? alpha(theme.palette.background.paper, 0.3)
            : alpha(theme.palette.background.paper, 0.6),
          boxShadow: `0 2px 6px ${alpha(theme.palette.common.black, 0.05)}`,
        }
      },
      '& .MuiTabs-indicator': {
        display: 'none',
      }
    },
    messageTabsContainer: {
      marginBottom: theme.spacing(2.5),
      border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
      borderRadius: 8,
      backgroundColor: isDark
        ? alpha(theme.palette.background.paper, 0.15)
        : alpha(theme.palette.background.default, 0.6),
      padding: theme.spacing(0.5),
    },
    messageTabIcon: {
      marginRight: theme.spacing(0.8),
      fontSize: '1.1rem',
    },
    colorAdorment: {
      width: 20,
      height: 20,
      borderRadius: 4,
      marginRight: theme.spacing(1),
    },
    fieldLabel: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing(1),
      fontWeight: 600,
      color: theme.palette.text.primary,
      fontSize: '0.9rem',
      '& svg': {
        marginRight: theme.spacing(0.8),
        color: theme.palette.primary.main,
        fontSize: '1.1rem',
        opacity: 0.9,
      }
    },
    helpIcon: {
      fontSize: 16,
      marginLeft: theme.spacing(0.5),
      color: alpha(theme.palette.text.secondary, 0.6),
      cursor: 'pointer',
      transition: 'color 0.2s',
      '&:hover': {
        color: theme.palette.primary.main
      }
    },
    infoTooltip: {
      maxWidth: 250,
      fontSize: '0.8rem',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      borderRadius: 8,
      padding: theme.spacing(1.2, 1.5),
      lineHeight: 1.5,
    },
    hintText: {
      fontSize: '0.8rem',
      color: theme.palette.text.secondary,
      marginTop: theme.spacing(0.8),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.6),
      padding: theme.spacing(0.8, 1.2),
      borderRadius: 8,
      backgroundColor: isDark
        ? alpha(theme.palette.background.paper, 0.15)
        : alpha(theme.palette.background.default, 0.7),
      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      '& svg': {
        fontSize: '1rem',
        color: theme.palette.primary.main,
        opacity: 0.8,
      }
    },
    closeButton: {
      color: theme.palette.primary.contrastText,
      backgroundColor: alpha(theme.palette.common.white, 0.1),
      borderRadius: 8,
      padding: 6,
      '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.2),
      }
    },
    titleWithIcon: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    switchRoot: {
      width: 42,
      height: 24,
      padding: 0,
      margin: 0,
      '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 2,
        '&.Mui-checked': {
          transform: 'translateX(18px)',
          color: '#fff',
          '& + .MuiSwitch-track': {
            backgroundColor: theme.palette.primary.main,
            opacity: 0.85,
            border: 0,
          },
        },
      },
      '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 20,
        height: 20,
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
      },
      '& .MuiSwitch-track': {
        borderRadius: 26,
        opacity: 0.38,
        backgroundColor: theme.palette.text.secondary,
        boxSizing: 'border-box',
        transition: 'all 0.2s',
      },
    },
    successAlert: {
      position: 'fixed',
      bottom: 20,
      right: 20,
      backgroundColor: alpha(theme.palette.success.main, 0.95),
      color: theme.palette.success.contrastText,
      padding: theme.spacing(1, 2),
      borderRadius: 8,
      display: 'flex',
      alignItems: 'center',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
      zIndex: 2000,
      gap: theme.spacing(1),
      '& .MuiSvgIcon-root': {
        fontSize: '1.2rem',
      },
      '& .MuiTypography-root': {
        fontWeight: 500,
        fontSize: '0.9rem',
      }
    },
    keywordsPanel: {
      marginTop: theme.spacing(1.5),
      marginBottom: theme.spacing(2),
      padding: theme.spacing(2),
      borderRadius: 10,
      backgroundColor: isDark
        ? alpha(theme.palette.background.paper, 0.15)
        : alpha(theme.palette.background.default, 0.6),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
    },
    keywordsHeader: {
      fontSize: '0.9rem',
      fontWeight: 600,
      color: theme.palette.text.primary,
      marginBottom: theme.spacing(1.5),
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.8),
      paddingBottom: theme.spacing(1.2),
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.25)}`,
      '& svg': {
        color: theme.palette.primary.main,
      }
    },
    keywordsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(0.75),
    },
    keywordChip: {
      height: 32,
      borderRadius: 16,
      backgroundColor: isDark
        ? alpha(theme.palette.background.paper, 0.25)
        : alpha(theme.palette.background.paper, 0.8),
      border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
      color: theme.palette.text.primary,
      fontSize: '0.8rem',
      fontWeight: 500,
      transition: 'all 0.15s ease',
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        borderColor: alpha(theme.palette.primary.main, 0.3),
        boxShadow: `0 2px 6px ${alpha(theme.palette.primary.main, 0.12)}`,
      },
      '& .MuiChip-icon': {
        color: theme.palette.primary.main,
        marginLeft: 4,
      },
      '& .MuiChip-label': {
        padding: '0 10px',
      }
    },
    keywordCategoryTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.8),
      fontSize: '0.85rem',
      fontWeight: 600,
      padding: theme.spacing(0.8, 1.5),
      borderRadius: 8,
      marginBottom: theme.spacing(1.2),
      marginTop: theme.spacing(1.8),
      '&:first-of-type': {
        marginTop: 0,
      },
      '& svg': {
        fontSize: '0.95rem',
      }
    },
    fieldTags: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(0.6),
      marginTop: theme.spacing(0.8),
      marginBottom: theme.spacing(0.8),
    },
    activeKeywordTag: {
      height: 26,
      fontSize: '0.75rem',
      fontWeight: 500,
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      color: theme.palette.primary.main,
      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
      borderRadius: 13,
      transition: 'all 0.15s ease',
      '& .MuiChip-label': {
        padding: theme.spacing(0, 0.75),
      },
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.15),
      }
    },
    disabledSection: {
      opacity: 0.5,
      pointerEvents: 'none',
      backgroundColor: isDark
        ? alpha(theme.palette.background.paper, 0.1)
        : alpha(theme.palette.background.default, 0.4),
    },
  };
});

// Palavras-chave disponíveis para as mensagens
const availableKeywords = [
  // Grupo Cliente
  { 
    key: '{{cliente}}', 
    label: 'Nome do Cliente', 
    description: 'Insere o nome do cliente automaticamente', 
    example: 'Olá, {{cliente}}!',
    icon: <AccessibilityNewIcon />,
    category: 'cliente'
  },
  { 
    key: '{{cliente.nome}}', 
    label: 'Primeiro nome do cliente', 
    description: 'Exibe apenas o primeiro nome do cliente', 
    example: 'Bom dia, {{cliente.nome}}!',
    icon: <AccessibilityNewIcon />,
    category: 'cliente'
  },
  { 
    key: '{{cliente.email}}', 
    label: 'Email do cliente', 
    description: 'Insere o endereço de email do cliente', 
    example: 'Confirmamos seu acesso em {{cliente.email}}',
    icon: <AlternateEmailIcon />,
    category: 'cliente'
  },
  
  // Grupo Atendimento
  { 
    key: '{{protocolo}}', 
    label: 'Protocolo', 
    description: 'Insere o número do protocolo do atendimento', 
    example: 'Seu protocolo: {{protocolo}}',
    icon: <NumbersIcon />,
    category: 'atendimento'
  },
  { 
    key: '{{setor}}', 
    label: 'Nome do Setor', 
    description: 'Insere o nome do setor atual', 
    example: 'Você está no setor de {{setor}}',
    icon: <QuestionAnswerIcon />,
    category: 'atendimento'
  },
  { 
    key: '{{atendente}}', 
    label: 'Nome do Atendente', 
    description: 'Insere o nome do atendente atual', 
    example: 'Meu nome é {{atendente}}, como posso ajudar?',
    icon: <AlternateEmailIcon />,
    category: 'atendimento'
  },
  
  // Grupo Empresa
  { 
    key: '{{empresa}}', 
    label: 'Nome da Empresa', 
    description: 'Insere o nome da sua empresa conforme cadastrado', 
    example: 'A {{empresa}} agradece seu contato',
    icon: <BusinessIcon />,
    category: 'empresa'
  },
  
  // Grupo Data e Hora
  { 
    key: '{{data}}', 
    label: 'Data Atual', 
    description: 'Insere a data atual no formato DD/MM/AAAA', 
    example: 'Hoje é {{data}}',
    icon: <EventAvailableIcon />,
    category: 'dataHora'
  },
  { 
    key: '{{hora}}', 
    label: 'Hora Atual', 
    description: 'Insere a hora atual no formato HH:MM', 
    example: 'Agora são {{hora}}',
    icon: <ScheduleIcon />,
    category: 'dataHora'
  },
  { 
    key: '{{saudacao}}', 
    label: 'Saudação Dinâmica', 
    description: 'Insere uma saudação de acordo com o horário atual', 
    example: '{{saudacao}}, em que posso ajudar?',
    icon: <AccessTimeIcon />,
    category: 'dataHora'
  },
];

// Categorias de palavras-chave
const keywordCategories = [
  {
    id: 'cliente',
    title: 'Cliente',
    icon: <AccessibilityNewIcon fontSize="small" />,
    color: '#4C6EF5'
  },
  {
    id: 'atendimento',
    title: 'Atendimento',
    icon: <QuestionAnswerIcon fontSize="small" />,
    color: '#FA5252'
  },
  {
    id: 'empresa',
    title: 'Empresa',
    icon: <BusinessIcon fontSize="small" />,
    color: '#7950F2'
  },
  {
    id: 'dataHora',
    title: 'Data e Hora',
    icon: <AccessTimeIcon fontSize="small" />,
    color: '#12B886'
  }
];

// Schema de validação condicional
const QueueSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Muito Curto(a)!")
    .max(400, "Muito Longo(a)!")
    .required("Obrigatório"),
  color: Yup.string().required(),
  greetingMessage: Yup.string().when('ignoreChatbot', {
    is: false,
    then: schema => schema.required("Este campo não pode estar vazio"),
    otherwise: schema => schema.notRequired()
  }),
  messageAccepting: Yup.string().when('ignoreChatbot', {
    is: false,
    then: schema => schema.required("Este campo não pode estar vazio"),
    otherwise: schema => schema.notRequired()
  }),
  closedMessage: Yup.string().when('ignoreChatbot', {
    is: false,
    then: schema => schema.required("Este campo não pode estar vazio"),
    otherwise: schema => schema.notRequired()
  }),
  description: Yup.string()
    .min(2, "Muito Curto(a)!")
    .max(400, "Muito Longo(a)!")
    .required("Obrigatório"),
  companyId: Yup.string(),
  ignoreChatbot: Yup.boolean(),
});

const QueueSchemaChatbot = Yup.object().shape({
  name: Yup.string()
    .min(2, "Muito Curto(a)!")
    .max(400, "Muito Longo(a)!")
    .required("Obrigatório"),
  color: Yup.string().required(),
  ignoreChatbot: Yup.boolean(),
});

const TextFieldWithKeywords = ({
  fieldName,
  label,
  placeholder,
  fieldRef,
  values,
  touched,
  errors,
  setFieldValue,
  handleFieldFocus,
  handleFieldBlur,
  handleSelectionChange,
  handleTextFieldKeyUp,
  handleTextFieldMouseUp,
  handleInsertKeyword,
  handleChangeValues,
  handleChange,
  focusedField,
  classes,
  multiline = true,
  minRows = 3,
  maxRows = 5,
  icon = <MessageIcon fontSize="small" />
}) => {
  const [showKeywords, setShowKeywords] = useState(false);

  const keywordsInText = React.useMemo(() => {
    if (!values[fieldName]) return [];
    const regex = /\{\{([^{}]+)\}\}/g;
    const matches = [];
    let match;
    while ((match = regex.exec(values[fieldName])) !== null) {
      if (!matches.some(m => m.value === match[0])) {
        matches.push({
          value: match[0],
          keyword: match[1]
        });
      }
    }
    return matches;
  }, [values[fieldName]]);

  const handleFocus = () => {
    handleFieldFocus(fieldName);
    setShowKeywords(true);
  };

  const handleBlur = (e) => {
    if (e.relatedTarget && e.relatedTarget.closest('.MuiChip-root')) {
      return;
    }
    handleFieldBlur();
    setTimeout(() => {
      setShowKeywords(false);
    }, 200);
  };

  return (
    <Box sx={{ position: 'relative', mb: 2.5 }}>
      <Typography className={classes.fieldLabel}>
        {icon} {label}
      </Typography>

      <Field
        as={TextField}
        placeholder={placeholder}
        multiline={multiline}
        minRows={minRows}
        maxRows={maxRows}
        fullWidth
        name={fieldName}
        error={touched[fieldName] && Boolean(errors[fieldName])}
        helperText={touched[fieldName] && errors[fieldName]}
        variant="outlined"
        size="small"
        className={`${classes.textField}`}
        inputRef={fieldRef}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onSelect={handleSelectionChange}
        onKeyUp={handleTextFieldKeyUp}
        onMouseUp={handleTextFieldMouseUp}
        onClick={handleSelectionChange}
        onChange={(e) => {
          // This ensures both Formik and local state stay in sync
          handleChange(e);  // Update Formik state
          if (handleChangeValues) {
            handleChangeValues(e, handleChange);  // Update local state
          }
        }}
        InputProps={{
          style: { fontSize: '0.9rem' },
        }}
      />

      {keywordsInText.length > 0 && (
        <Box className={classes.fieldTags}>
          {keywordsInText.map((item, index) => (
            <Chip
              key={`${item.value}-${index}`}
              size="small"
              label={item.value}
              className={classes.activeKeywordTag}
              icon={<LocalOfferIcon sx={{ fontSize: '0.75rem' }} />}
            />
          ))}
        </Box>
      )}

      {showKeywords && (
        <Box className={classes.keywordsPanel}>
          <Box className={classes.keywordsHeader}>
            <LocalOfferIcon sx={{ fontSize: '1rem' }} />
            <Typography>
              Palavras-chave disponíveis
            </Typography>
          </Box>

          {keywordCategories.map(category => {
            const categoryKeywords = availableKeywords.filter(
              keyword => keyword.category === category.id
            );

            if (categoryKeywords.length === 0) return null;

            return (
              <Box key={category.id} sx={{ mb: 1 }}>
                <Box
                  className={classes.keywordCategoryTitle}
                  sx={{
                    color: category.color,
                    borderLeft: `3px solid ${category.color}`,
                    backgroundColor: theme => theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : '#edecec'
                  }}
                >
                  {category.icon}
                  <Typography variant="subtitle2">
                    {category.title}
                  </Typography>
                </Box>

                <Box className={classes.keywordsContainer}>
                  {categoryKeywords.map(keyword => (
                    <Chip
                      key={keyword.key}
                      label={keyword.label}
                      icon={keyword.icon}
                      className={classes.keywordChip}
                      onClick={() => handleInsertKeyword(keyword.key, setFieldValue, values)}
                      title={keyword.description}
                    />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const CreateOrEditQueueModal = ({ open, onClose, queueId }) => {
  const { user: loggedInUser } = useContext(AuthContext);
  const { classes } = useStyles();

  const initialState = {
    companyId: loggedInUser.companyId,
    name: "",
    color: "",
    isChatbot: false,
    chatbotId: "",
    greetingMessage: "",
    messageAccepting: "",
    closedMessage: "",
    description: "",
    ignoreChatbot: false, // Novo campo adicionado
  };

  const defaultStart = new Date(Date.UTC(2024, 9, 25, 8, 0));
  const defaultEnd = new Date(Date.UTC(2024, 9, 25, 18, 0));
  const defaultIntervalStart = new Date(Date.UTC(2024, 9, 25, 12, 0));
  const defaultIntervalEnd = new Date(Date.UTC(2024, 9, 25, 14, 0));

  const daysOfWeek = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday'
  ];

  const initialStateSchedule = daysOfWeek.reduce((schedule, day) => {
    schedule[day] = {
      open: day === 'saturday' || day === 'sunday' ? false : true,
      start: defaultStart,
      end: defaultEnd,
      interval: true,
      intervalStart: defaultIntervalStart,
      intervalEnd: defaultIntervalEnd
    };
    return schedule;
  }, {});

  const [queue, setQueue] = useState(initialState);
  const [scheduleQueue, setScheduleQueue] = useState(initialStateSchedule);
  const [tab, setTab] = useState('1');
  const [chatbots, setChatbots] = useState([]);
  const [messageTab, setMessageTab] = useState(0);
  
  // Estados para gerenciar palavras-chave
  const [activeField, setActiveField] = useState(null);
  const [fieldSelectionStart, setFieldSelectionStart] = useState(0);
  const [fieldSelectionEnd, setFieldSelectionEnd] = useState(0);
  const [insertedKeyword, setInsertedKeyword] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  // Refs para campos de texto
  const greetingMessageRef = useRef(null);
  const messageAcceptingRef = useRef(null);
  const closedMessageRef = useRef(null);
  const descriptionRef = useRef(null);
  
  // Timeout para mensagem de sucesso
  const successTimeoutRef = useRef(null);

  useEffect(() => {
    (async () => {
      setQueue((prevState) => ({ ...prevState, chatbotId: null }));
      try {
        const { data } = await api.get(`/chatbot/`);
        setChatbots(data);
      } catch (err) {
        ToastError(err);
      }
      if (!queueId) return;
      try {
        const { data } = await api.get(`/queue/${queueId}`);
        setQueue(prevState => ({ ...prevState, ...data }));
        if (data?.schedules) {
          const queueSchedules = data.schedules;
          setScheduleQueue(queueSchedules)
        }
        setQueue((prevState) => ({ ...prevState, isChatbot: data.isChatbot }));
        if (data?.chatbotId) setQueue((prevState) => ({ ...prevState, chatbotId: data.chatbotId }));
        // Adicionar suporte ao campo ignoreChatbot
        if (data?.ignoreChatbot !== undefined) setQueue((prevState) => ({ ...prevState, ignoreChatbot: data.ignoreChatbot }));
      } catch (err) {
        ToastError(err);
      }
    })();

    return () => {
      setQueue(initialState);
      setTab('1');
      setMessageTab(0);
      // Limpar timeouts pendentes
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, [queueId, open]);

  const handleClose = () => {
    onClose();
    setQueue(initialState);
    setTab('1');
    setMessageTab(0);
    setActiveField(null);
    
    // Limpar timeouts pendentes
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
    }
  };

  const handleSaveQueue = async (values) => {
    try {
      if (queueId) {
        await api.put(`/queue/${queueId}`, values);
      } else {
        await api.post("/queue", values);
      }
      ToastSuccess(i18n.t("queueModal.success") || "Queue saved successfully");
      handleClose();
    } catch (err) {
      ToastError(err);
    }
  };

  const handleChangeTab = (e, newValue) => setTab(newValue);
  const handleMessageTabChange = (e, newValue) => setMessageTab(newValue);

  const handleChangeValues = (e, callback) => {
    const { name, value } = e.target;
    setQueue((prevState) => ({ ...prevState, [name]: value }));
    callback(e);
  }
  
  // Gerenciar campos de texto para palavras-chave
  // Obter referência para campo de texto
  const getTextFieldRefByName = (name) => {
    switch (name) {
      case 'greetingMessage': return greetingMessageRef;
      case 'messageAccepting': return messageAcceptingRef;
      case 'closedMessage': return closedMessageRef;
      case 'description': return descriptionRef;
      default: return null;
    }
  };

  // Monitorar mudanças na seleção de texto
  const handleSelectionChange = (e) => {
    if (activeField) {
      setFieldSelectionStart(e.target.selectionStart);
      setFieldSelectionEnd(e.target.selectionEnd);
    }
  };
  
  // Atualizar posição do cursor no teclado
  const handleTextFieldKeyUp = (e) => {
    setFieldSelectionStart(e.target.selectionStart);
    setFieldSelectionEnd(e.target.selectionEnd);
  };
  
  // Capturar seleção após arrastar mouse
  const handleTextFieldMouseUp = (e) => {
    setFieldSelectionStart(e.target.selectionStart);
    setFieldSelectionEnd(e.target.selectionEnd);
  };

  // FIXED: Inserir palavra-chave no campo ativo
  const handleInsertKeyword = (keyword, setFieldValue) => {
    if (!activeField || !setFieldValue) return;
    
    // Sempre obter o valor mais atual do Formik
    const currentValue = queue[activeField] || '';
    
    // Obter posição atual do cursor
    let currentSelectionStart = fieldSelectionStart;
    let currentSelectionEnd = fieldSelectionEnd;
    
    // Tentar obter a seleção mais atualizada do elemento ativo
    const textFieldRef = getTextFieldRefByName(activeField);
    if (textFieldRef?.current) {
      const inputElement = textFieldRef.current.querySelector('textarea');
      if (inputElement) {
        // Se o input estiver ativo, usar sua posição de seleção atual
        if (document.activeElement === inputElement) {
          currentSelectionStart = inputElement.selectionStart;
          currentSelectionEnd = inputElement.selectionEnd;
          
          // Atualizar estado com as posições mais recentes
          setFieldSelectionStart(currentSelectionStart);
          setFieldSelectionEnd(currentSelectionEnd);
        }
      }
    }
    
    // Inserir a palavra-chave na posição do cursor ou substituir a seleção
    const beforeText = currentValue.substring(0, currentSelectionStart);
    const afterText = currentValue.substring(currentSelectionEnd);
    const newValue = beforeText + keyword + afterText;
    
    // IMPORTANTE: Atualizar PRIMEIRO o estado do Formik
    // Isso é crucial para manter a sincronização do estado do formulário
    setFieldValue(activeField, newValue);
    
    // DEPOIS atualizar o estado local do componente
    // Isso garante que tanto o Formik quanto o estado local estejam sincronizados
    // Usando uma atualização funcional para garantir que estamos trabalhando com o estado mais recente
    setQueue(prev => ({ ...prev, [activeField]: newValue }));
    
    // Calcular a nova posição do cursor (após a palavra-chave inserida)
    const newCursorPosition = currentSelectionStart + keyword.length;
    
    // Refocar o campo e posicionar o cursor após a palavra-chave inserida
    // Usando um pequeno timeout para garantir que o DOM foi atualizado
    setTimeout(() => {
      const textFieldRef = getTextFieldRefByName(activeField);
      if (textFieldRef?.current) {
        const inputElement = textFieldRef.current.querySelector('textarea');
        if (inputElement) {
          inputElement.focus();
          inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
          
          // Atualizar estado com a nova posição do cursor para manter tudo sincronizado
          setFieldSelectionStart(newCursorPosition);
          setFieldSelectionEnd(newCursorPosition);
        }
      }
    }, 50);
    
    // Mostrar notificação de sucesso
    setInsertedKeyword(keyword);
    setShowSuccess(true);
    
    // Limpar mensagem após delay
    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
    }
    
    successTimeoutRef.current = setTimeout(() => {
      setShowSuccess(false);
    }, 2000);
  };

  // Manipular foco no campo
  const handleFieldFocus = (fieldName) => {
    setFocusedField(fieldName);
    setActiveField(fieldName);
    
    // Armazenar posições de seleção atual
    const textFieldRef = getTextFieldRefByName(fieldName);
    if (textFieldRef?.current) {
      const inputElement = textFieldRef.current.querySelector('textarea');
      if (inputElement) {
        setFieldSelectionStart(inputElement.selectionStart);
        setFieldSelectionEnd(inputElement.selectionEnd);
      }
    }
  };

  const handleFieldBlur = () => {
    setFocusedField(null);
  };

  // Determinar se as mensagens devem ser mostradas
  const shouldShowMessages = !queue.isChatbot && !queue.ignoreChatbot;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className={classes.dialogRoot}
      fullWidth
      BackdropProps={{
        style: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Box className={classes.titleWithIcon}>
          <SettingsIcon fontSize="small" />
          <Typography variant="h6">
            {queueId ? i18n.t("queueModal.title.edit") : i18n.t("queueModal.title.add")}
          </Typography>
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleClose}
          aria-label="close"
          className={classes.closeButton}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Formik
        initialValues={queue}
        enableReinitialize={true}
        validationSchema={queue.isChatbot ? QueueSchemaChatbot : QueueSchema}
        onSubmit={(values, actions) => {
          setTimeout(() => {
            let value = { ...values, schedules: scheduleQueue };
            handleSaveQueue(value);
            actions.setSubmitting(false);
          }, 400);
        }}
      >
        {({ values, handleChange, errors, touched, isSubmitting, setFieldValue }) => (
          <Form>
            <TabContext value={tab}>
              <Box className={classes.messageTabsContainer}>
                <TabList 
                  onChange={handleChangeTab} 
                  aria-label="tabsEditQueues" 
                  className={classes.messageTabs} 
                  centered
                >
                  <Tab 
                    icon={<FormatListNumberedIcon className={classes.messageTabIcon} />} 
                    iconPosition="start" 
                    label={i18n.t("queueModal.form.tabs.information")} 
                    value="1" 
                  />
                  <Tab 
                    icon={<WatchLaterIcon className={classes.messageTabIcon} />} 
                    iconPosition="start" 
                    label={i18n.t("queueModal.form.tabs.schedules")} 
                    value="2" 
                  />
                </TabList>
              </Box>
              
              <TabPanel value="1">
                <DialogContent className={classes.dialogContent}>
                  <Field style={{ display: "none" }} as={TextField} type="hidden" name="companyId" value={loggedInUser.companyId} />
                  
                  <Paper elevation={0} className={classes.formSection}>
                    <Typography variant="subtitle1" className={classes.sectionTitle}>
                      <SettingsIcon className={classes.icon} />
                      {i18n.t("queueModal.form.basicSettings") || "Basic Settings"}
                    </Typography>
                    
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={8}>
                        <Typography className={classes.fieldLabel}>
                          <BusinessIcon fontSize="small" /> {i18n.t("queueModal.form.name")}
                        </Typography>
                        <Field
                          as={TextField}
                          placeholder={i18n.t("queueModal.form.namePlaceholder") || "Enter a name for this sector"}
                          name="name"
                          fullWidth
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name && errors.name}
                          variant="outlined"
                          size="small"
                          className={classes.textField}
                          value={queue.name}
                          onChange={e => handleChangeValues(e, handleChange)}
                        />
                      </Grid>
                      
                      <Grid item xs={12} md={4}>
                        <Typography className={classes.fieldLabel}>
                          <LocalOfferIcon fontSize="small" /> {i18n.t("queueModal.form.color") || "Color"}
                        </Typography>
                        <MuiColorInput
                          format="hex"
                          name="color"
                          size="small"
                          value={queue.color}
                          onChange={color => {
                            setQueue((prev) => ({ ...prev, color }));
                            setFieldValue("color", color);
                          }}
                          variant="outlined"
                          error={touched.color && Boolean(errors.color)}
                          helperText={touched.color && errors.color}
                          fullWidth
                          className={classes.textField}
                        />
                      </Grid>
                    </Grid>

                    <Box mt={2}>
                      <TextFieldWithKeywords
                        fieldName="description"
                        label={i18n.t("queueModal.form.description") || "Description"}
                        placeholder={i18n.t("queueModal.form.descriptionPlaceholder") || "Enter a description for this sector"}
                        fieldRef={descriptionRef}
                        values={values}
                        touched={touched}
                        errors={errors}
                        setFieldValue={setFieldValue}
                        handleFieldFocus={handleFieldFocus}
                        handleFieldBlur={handleFieldBlur}
                        handleSelectionChange={handleSelectionChange}
                        handleTextFieldKeyUp={handleTextFieldKeyUp}
                        handleTextFieldMouseUp={handleTextFieldMouseUp}
                        handleInsertKeyword={handleInsertKeyword}
                        handleChangeValues={handleChangeValues}
                        handleChange={handleChange}
                        focusedField={focusedField}
                        classes={classes}
                        icon={<InfoOutlinedIcon fontSize="small" />}
                      />
                    </Box>

                    <Box mt={2}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={queue.isChatbot}
                            onChange={() => {
                              const newValue = !queue.isChatbot;
                              setQueue((prevState) => ({ ...prevState, isChatbot: newValue }));
                              setFieldValue("isChatbot", newValue);
                            }}
                            color="primary"
                            size="small"
                          />
                        }
                        label={
                          <Box display="flex" alignItems="center" gap={0.6}>
                            <SmartToyIcon sx={{ fontSize: '1.1rem', color: theme => theme.palette.primary.main }} />
                            <Typography sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                              {i18n.t("queueModal.chatbot.use") || "Use Chatbot"}
                            </Typography>
                            <Tooltip
                              title={i18n.t("queueModal.chatbot.tooltip") || "Assign a chatbot to automatically handle this queue"}
                              arrow
                              classes={{ tooltip: classes.infoTooltip }}
                            >
                              <HelpOutlineIcon className={classes.helpIcon} />
                            </Tooltip>
                          </Box>
                        }
                      />

                      {queue.isChatbot && (
                        <Box mt={2}>
                          <Typography className={classes.fieldLabel}>
                            <SmartToyIcon fontSize="small" /> {i18n.t("queueModal.chatbot.select") || "Select Chatbot"}
                          </Typography>
                          <FormControl
                            fullWidth
                            variant="outlined"
                            size="small"
                            className={classes.textField}
                          >
                            <Select
                              value={queue.chatbotId || ''}
                              onChange={(e) => {
                                const newValue = e.target.value;
                                setQueue((prevState) => ({ ...prevState, chatbotId: newValue }));
                                setFieldValue("chatbotId", newValue);
                              }}
                              displayEmpty
                            >
                              <MenuItem value="" disabled>
                                {i18n.t("queueModal.chatbot.selectPlaceholder") || "Select a chatbot"}
                              </MenuItem>
                              {chatbots.map((chatbot) => (
                                <MenuItem key={chatbot.id} value={chatbot.id}>{chatbot.name}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Typography className={classes.hintText}>
                            <TipsAndUpdatesIcon fontSize="small" />
                            {i18n.t("queueModal.chatbot.hint") || "The selected chatbot will automatically handle incoming conversations in this sector."}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Novo checkbox para ignorar mensagens */}
                    {!queue.isChatbot && (
                      <Box mt={2}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={queue.ignoreChatbot}
                              onChange={() => {
                                const newValue = !queue.ignoreChatbot;
                                setQueue((prevState) => ({ ...prevState, ignoreChatbot: newValue }));
                                setFieldValue("ignoreChatbot", newValue);
                                
                                // Limpar valores dos campos de mensagem quando ignoreChatbot for true
                                if (newValue) {
                                  setFieldValue("greetingMessage", "");
                                  setFieldValue("messageAccepting", "");
                                  setFieldValue("closedMessage", "");
                                  setQueue((prevState) => ({ 
                                    ...prevState, 
                                    greetingMessage: "",
                                    messageAccepting: "",
                                    closedMessage: ""
                                  }));
                                }
                              }}
                              color="primary"
                              size="small"
                            />
                          }
                          label={
                            <Box display="flex" alignItems="center" gap={0.6}>
                              <DoNotDisturbIcon sx={{ fontSize: '1.1rem', color: theme => theme.palette.warning.main }} />
                              <Typography sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                {i18n.t("queueModal.ignoreChatbot.use") || "Não usar mensagens neste setor"}
                              </Typography>
                              <Tooltip
                                title={i18n.t("queueModal.ignoreChatbot.tooltip") || "Ao marcar esta opção, nenhuma mensagem automática será enviada para contatos neste setor"}
                                arrow
                                classes={{ tooltip: classes.infoTooltip }}
                              >
                                <HelpOutlineIcon className={classes.helpIcon} />
                              </Tooltip>
                            </Box>
                          }
                        />
                        
                        {queue.ignoreChatbot && (
                          <Typography className={classes.hintText} sx={{ mt: 1 }}>
                            <TipsAndUpdatesIcon fontSize="small" />
                            {i18n.t("queueModal.ignoreChatbot.hint") || "Com esta opção ativada, nenhuma mensagem de saudação, aceitação ou horário de funcionamento será enviada automaticamente."}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Paper>

                  {shouldShowMessages && (
                    <Paper elevation={0} className={classes.formSection}>
                      <Typography variant="subtitle1" className={classes.sectionTitle}>
                        <MessageIcon className={classes.icon} />
                        {i18n.t("queueModal.form.messages") || "Messages"}
                      </Typography>

                      <Box className={classes.messageTabsContainer}>
                        <Tabs
                          value={messageTab}
                          onChange={handleMessageTabChange}
                          variant="scrollable"
                          scrollButtons="auto"
                          className={classes.messageTabs}
                        >
                          <Tab
                            label={i18n.t("queueModal.form.greetingMessage") || "Welcome"}
                            icon={<ForumIcon className={classes.messageTabIcon} />}
                            iconPosition="start"
                          />

                          <Tab
                            label={i18n.t("queueModal.form.messageAccepting") || "Acceptance"}
                            icon={<CheckCircleIcon className={classes.messageTabIcon} />}
                            iconPosition="start"
                          />

                          <Tab
                            label={i18n.t("queueModal.form.closedMessage") || "Closed Hours"}
                            icon={<ScheduleIcon className={classes.messageTabIcon} />}
                            iconPosition="start"
                          />
                        </Tabs>
                      </Box>

                      {messageTab === 0 && (
                        <TextFieldWithKeywords
                          fieldName="greetingMessage"
                          label={i18n.t("queueModal.form.greetingMessage") || "Welcome Message"}
                          placeholder={i18n.t("queueModal.form.greetingMessagePlaceholder") || "Message sent when a customer enters this sector"}
                          fieldRef={greetingMessageRef}
                          values={values}
                          touched={touched}
                          errors={errors}
                          setFieldValue={setFieldValue}
                          handleFieldFocus={handleFieldFocus}
                          handleFieldBlur={handleFieldBlur}
                          handleSelectionChange={handleSelectionChange}
                          handleTextFieldKeyUp={handleTextFieldKeyUp}
                          handleTextFieldMouseUp={handleTextFieldMouseUp}
                          handleInsertKeyword={handleInsertKeyword}
                          handleChangeValues={handleChangeValues}
                          handleChange={handleChange}
                          focusedField={focusedField}
                          classes={classes}
                          icon={<ForumIcon fontSize="small" />}
                        />
                      )}

                      {messageTab === 1 && (
                        <TextFieldWithKeywords
                          fieldName="messageAccepting"
                          label={i18n.t("queueModal.form.messageAccepting") || "Acceptance Message"}
                          placeholder={i18n.t("queueModal.form.messageAcceptingPlaceholder") || "Message sent when an agent accepts the ticket"}
                          fieldRef={messageAcceptingRef}
                          values={values}
                          touched={touched}
                          errors={errors}
                          setFieldValue={setFieldValue}
                          handleFieldFocus={handleFieldFocus}
                          handleFieldBlur={handleFieldBlur}
                          handleSelectionChange={handleSelectionChange}
                          handleTextFieldKeyUp={handleTextFieldKeyUp}
                          handleTextFieldMouseUp={handleTextFieldMouseUp}
                          handleInsertKeyword={handleInsertKeyword}
                          handleChangeValues={handleChangeValues}
                          handleChange={handleChange}
                          focusedField={focusedField}
                          classes={classes}
                          icon={<CheckCircleIcon fontSize="small" />}
                        />
                      )}

                      {messageTab === 2 && (
                        <TextFieldWithKeywords
                          fieldName="closedMessage"
                          label={i18n.t("queueModal.form.closedMessage") || "Closed Hours Message"}
                          placeholder={i18n.t("queueModal.form.closedMessagePlaceholder") || "Message sent when sector is outside working hours"}
                          fieldRef={closedMessageRef}
                          values={values}
                          touched={touched}
                          errors={errors}
                          setFieldValue={setFieldValue}
                          handleFieldFocus={handleFieldFocus}
                          handleFieldBlur={handleFieldBlur}
                          handleSelectionChange={handleSelectionChange}
                          handleTextFieldKeyUp={handleTextFieldKeyUp}
                          handleTextFieldMouseUp={handleTextFieldMouseUp}
                          handleInsertKeyword={handleInsertKeyword}
                          handleChangeValues={handleChangeValues}
                          handleChange={handleChange}
                          focusedField={focusedField}
                          classes={classes}
                          icon={<ScheduleIcon fontSize="small" />}
                        />
                      )}
                    </Paper>
                  )}
                </DialogContent>
              </TabPanel>
              
              <TabPanel value="2">
                <DialogContent className={classes.dialogContent}>
                  <Paper elevation={0} className={classes.formSection}>
                    <Typography variant="subtitle1" className={classes.sectionTitle}>
                      <WatchLaterIcon className={classes.icon} />
                      {i18n.t("queueModal.form.scheduleTitle") || "Working Hours"}
                    </Typography>
                    
                    <QueueScheduleComponent
                      values={scheduleQueue}
                      onChangeValue={setScheduleQueue}
                    />
                    
                    <Typography className={classes.hintText} sx={{ mt: 2 }}>
                      <TipsAndUpdatesIcon fontSize="small" />
                      {i18n.t("queueModal.form.scheduleHint") || "Outside these hours, customers will receive your closed hours message."}
                    </Typography>
                  </Paper>
                </DialogContent>
              </TabPanel>
            </TabContext>
            
            <DialogActions className={classes.dialogActions}>
              <Button
                onClick={handleClose}
                color="inherit"
                disabled={isSubmitting}
                variant="outlined"
                className={classes.cancelButton}
                startIcon={<CloseIcon fontSize="small" />}
              >
                {i18n.t("queueModal.buttons.cancel")}
              </Button>
              
              <Button
                type="submit"
                color="primary"
                disabled={isSubmitting}
                variant="contained"
                className={classes.saveButton}
                startIcon={isSubmitting ? <CircularProgress size={18} /> : null}
              >
                {queueId
                  ? i18n.t("queueModal.buttons.okEdit")
                  : i18n.t("queueModal.buttons.okAdd")}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>

      {showSuccess && (
        <Box className={classes.successAlert}>
          <CheckCircleIcon />
          <Typography>
            {i18n.t("queueModal.keywordSuccess") || `Keyword ${insertedKeyword} inserted!`}
          </Typography>
        </Box>
      )}
    </Dialog>
  );
};

export default CreateOrEditQueueModal;